import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { SupabaseService } from 'app/core/supabase/supabase.service';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

/**
 * Auth Interceptor
 *
 * Adds Supabase access token to HTTP requests and handles 401 errors
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const supabaseService = inject(SupabaseService);

    // Skip interceptor for Supabase API calls (handled by Supabase client)
    if (req.url.includes('supabase.co') || req.url.includes('127.0.0.1:54321')) {
        return next(req);
    }

    // For other API calls, get the Supabase session token
    return from(supabaseService.getSession()).pipe(
        switchMap((session) => {
            let newReq = req;

            // If session exists and has access token, add Authorization header
            if (session?.access_token) {
                newReq = req.clone({
                    headers: req.headers.set(
                        'Authorization',
                        `Bearer ${session.access_token}`
                    ),
                });
            }

            // Process the request
            return next(newReq).pipe(
                catchError((error) => {
                    // Handle 401 Unauthorized responses
                    if (error instanceof HttpErrorResponse && error.status === 401) {
                        // Sign out the user
                        authService.signOut().subscribe(() => {
                            // Reload the app to clear state
                            location.reload();
                        });
                    }

                    return throwError(() => error);
                })
            );
        })
    );
};
